"use client";

/**
 * The `+` / Mod+T workspace menu — a command-palette-style flow, not a form.
 *
 * Step 1 (search): one live search field over existing workspaces plus a
 *   persistent `Create "<query>"` row. Typing a name and pressing ↵ advances to
 *   the layout step; selecting an existing result opens it as a terminal tab.
 *
 * Step 2 (layout): pick how the new workspace is laid out — Snapping canvas,
 *   Free canvas, or Grid — and it is created and opened. `←` goes back.
 *
 * ONE WORKSPACE == ONE TAB: this only ever opens a local tab.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Spinner,
} from "@itoresearch/design-system";
import {
  ArrowLeft,
  ChevronRight,
  FileTextIcon,
  LayoutGridIcon,
  MagnetIcon,
  PlusIcon,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState } from "react";

import { useDebouncedValue } from "../../hooks/use-debounced-value";
import { workspaces } from "../../lib/workspaces";
import type {
  CreateWorkspaceRequest,
  WorkspaceKind,
  WorkspaceRigidity,
  WorkspaceSummary,
} from "../../lib/workspaces";
import { openWorkspaceTab } from "./tabs-store";

/** A creation layout choice: the kind + rigidity a new workspace is seeded at. */
interface LayoutOption {
  id: string;
  label: string;
  description: string;
  kind: WorkspaceKind;
  rigidity: WorkspaceRigidity;
  icon: LucideIcon;
}

export const LAYOUT_OPTIONS: LayoutOption[] = [
  {
    id: "snapping",
    label: "Snapping canvas",
    description: "Infinite board — widgets snap to a grid",
    kind: "canvas",
    rigidity: "canvas_grid",
    icon: MagnetIcon,
  },
  {
    id: "free",
    label: "Free canvas",
    description: "Infinite board — place widgets anywhere",
    kind: "canvas",
    rigidity: "canvas",
    icon: LayoutGridIcon,
  },
  {
    id: "grid",
    label: "Grid",
    description: "Structured, auto-laid-out page",
    kind: "page",
    rigidity: "grid",
    icon: FileTextIcon,
  },
];

type Step = "search" | "layout";

export interface WorkspacePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WorkspacePicker({ open, onOpenChange }: WorkspacePickerProps) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [step, setStep] = useState<Step>("search");
  const [pendingTitle, setPendingTitle] = useState("");

  // Fresh flow every time the menu opens.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setStep("search");
    setPendingTitle("");
  }, [open]);

  const debouncedQuery = useDebouncedValue(query, 150);

  const listQuery = useQuery({
    queryKey: ["workspaces", "list", debouncedQuery],
    enabled: open && step === "search",
    staleTime: 15_000,
    queryFn: () =>
      workspaces.list({
        q: debouncedQuery.trim() || undefined,
        limit: 50,
      }),
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateWorkspaceRequest) => workspaces.create(input),
    onSuccess: (workspace) => {
      void queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      openWorkspaceTab(workspace.workspaceId, {
        title: workspace.title,
        kind: workspace.kind,
        ephemeral: workspace.ephemeral,
      });
      onOpenChange(false);
    },
  });

  const trimmed = query.trim();
  const selectExisting = (workspace: WorkspaceSummary) => {
    openWorkspaceTab(workspace.workspaceId, {
      title: workspace.title,
      kind: workspace.kind,
      ephemeral: workspace.ephemeral,
    });
    onOpenChange(false);
  };

  const goToLayouts = () => {
    setPendingTitle(trimmed);
    setStep("layout");
  };

  const createWith = (option: LayoutOption) => {
    if (createMutation.isPending) return;
    const title =
      pendingTitle ||
      (option.kind === "page" ? "Untitled page" : "Untitled canvas");
    createMutation.mutate({
      kind: option.kind,
      rigidity: option.rigidity,
      title,
    });
  };

  const results = listQuery.data?.data ?? [];

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title={step === "search" ? "Open workspace" : "Choose a layout"}
      description="Search, open, or create a workspace"
    >
      <Command
        shouldFilter={false}
        loop
        onKeyDown={(event) => {
          if (step === "layout" && event.key === "ArrowLeft") {
            event.preventDefault();
            setStep("search");
          }
        }}
      >
        {step === "search" ? (
          <CommandInput
            autoFocus
            value={query}
            onValueChange={setQuery}
            placeholder="Search workspaces or type to create…"
          />
        ) : (
          <div className="flex items-center gap-1 border-b px-3 py-2 text-xs text-muted-foreground">
            <button
              type="button"
              onClick={() => setStep("search")}
              className="inline-flex items-center gap-1 rounded-sm px-1 py-0.5 hover:bg-muted hover:text-foreground"
            >
              <ArrowLeft className="size-3.5" />
              Back
            </button>
            <span className="opacity-50">/</span>
            <span className="truncate font-medium text-foreground">
              {pendingTitle ? `Create “${pendingTitle}”` : "New workspace"}
            </span>
          </div>
        )}

        <CommandList>
          {step === "search" ? (
            <>
              <CommandGroup heading="Create">
                <CommandItem value={`create ${trimmed}`} onSelect={goToLayouts}>
                  <PlusIcon />
                  <span className="flex-1 truncate">
                    {trimmed ? `Create “${trimmed}”` : "Create new workspace…"}
                  </span>
                  <ChevronRight className="size-3.5 text-muted-foreground" />
                </CommandItem>
              </CommandGroup>
              <CommandSeparator alwaysRender />
              <CommandGroup heading="Workspaces">
                {listQuery.isPending && debouncedQuery.trim().length > 0 ? (
                  <CommandItem forceMount disabled>
                    <Spinner className="size-4" />
                    Searching…
                  </CommandItem>
                ) : listQuery.isError ? (
                  <CommandItem forceMount disabled>
                    Search is unavailable right now.
                  </CommandItem>
                ) : results.length === 0 ? (
                  <CommandItem forceMount disabled>
                    {query.trim()
                      ? `No workspaces match “${query.trim()}”.`
                      : "No workspaces yet."}
                  </CommandItem>
                ) : (
                  results.map((workspace) => (
                    <CommandItem
                      key={workspace.workspaceId}
                      value={`${workspace.title} ${workspace.slug ?? ""} ${workspace.workspaceId}`}
                      onSelect={() => selectExisting(workspace)}
                    >
                      {workspace.kind === "page" ? (
                        <FileTextIcon className="text-sky-500" />
                      ) : (
                        <LayoutGridIcon className="text-violet-500" />
                      )}
                      <span className="flex-1 truncate">{workspace.title}</span>
                      {workspace.slug ? (
                        <span className="truncate font-mono text-xs text-muted-foreground">
                          {workspace.slug}
                        </span>
                      ) : null}
                      {workspace.ephemeral ? (
                        <Badge variant="outline">ephemeral</Badge>
                      ) : null}
                      <Badge variant="secondary">{workspace.kind}</Badge>
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            </>
          ) : (
            <>
              <CommandGroup heading="Layout">
                {LAYOUT_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <CommandItem
                      key={option.id}
                      value={option.label}
                      disabled={createMutation.isPending}
                      onSelect={() => createWith(option)}
                    >
                      <Icon />
                      <span className="flex-1 truncate">{option.label}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {option.description}
                      </span>
                      {createMutation.isPending ? (
                        <Spinner className="size-3.5" />
                      ) : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {createMutation.isError ? (
                <CommandItem forceMount disabled>
                  <span className="text-destructive">
                    {createMutation.error instanceof Error
                      ? createMutation.error.message
                      : "Could not create the workspace."}
                  </span>
                </CommandItem>
              ) : null}
            </>
          )}
          {step === "search" && listQuery.isError && results.length === 0 ? (
            <CommandEmpty>Search is unavailable right now.</CommandEmpty>
          ) : null}
        </CommandList>

        <div className="flex items-center gap-3 border-t px-3 py-1.5 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <kbd className="rounded-sm border bg-muted px-1 font-mono">↑↓</kbd>{" "}
            navigate
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className="rounded-sm border bg-muted px-1 font-mono">↵</kbd>{" "}
            select
          </span>
          {step === "layout" && (
            <span className="inline-flex items-center gap-1">
              <kbd className="rounded-sm border bg-muted px-1 font-mono">←</kbd>{" "}
              back
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <kbd className="rounded-sm border bg-muted px-1 font-mono">esc</kbd>{" "}
            close
          </span>
        </div>
      </Command>
    </CommandDialog>
  );
}
